/**
 * Returns modified version of the input string that's split into three lines using '\n'.
 *
 * Shorter text is split into two lines, while everything less than 20 characters is returned as is.
 */
const ensureThreeLines = (text) => {
    const findIdealIndex = (sentence, idealIndex) => {
        let actualIndex = sentence.length;

        [...sentence].forEach((char, index) => {
            if (char === ' ' && Math.abs(index - idealIndex) < Math.abs(actualIndex - idealIndex)) {
                actualIndex = index;
            }
        });

        return actualIndex;
    };

    if (text.length < 20) {
        return text;
    }

    if (text.length < 40) {
        const idealIndex = findIdealIndex(text, Math.floor(text.length / 2));

        return `${text.slice(0, idealIndex)}\n${text.slice(idealIndex)}`;
    }

    const idealFirstIndex = findIdealIndex(text, Math.floor(text.length / 3));
    const idealSecondIndex = findIdealIndex(text, 2 * Math.floor(text.length / 3));

    return `${text.slice(0, idealFirstIndex)}\n${text.slice(idealFirstIndex, idealSecondIndex)}\n${text.slice(idealSecondIndex)}`;
};

/**
 * Returns a string representing how many days/weeks/months passed since the provided date.
 *
 * Return value can be 'today' or 'X years|months|weeks|days' ago
 *
 * @param {'YYYY-MM-DD'} date
 * @returns A string denoting how many days/weeks/months ago was the date
 */
function getAgo(date) {
    const msPerDay = 24 * 60 * 60 * 1000;
    const days = Math.round((new Date() - new Date(date)) / msPerDay);

    if (days === 0) return 'today';

    let unit;
    let value;

    if (days < 7) [unit, value] = ['day', days];
    else if (days < 31) [unit, value] = ['week', Math.round(days / 7)];
    else if (days < 365) [unit, value] = ['month', Math.round(days / 30.5)];
    else [unit, value] = ['year', Math.round(days / 365)];

    if (value > 1) unit = `${unit}s`;

    return `${value} ${unit} ago`;
}

/**
 * Converts seconds to a timestamp string in the format 'HH:MM:SS'.
 *
 * @param {number} timestamp - The number of seconds to convert.
 * @returns {string} A string representing the timestamp in 'HH:MM:SS'
 */
function secondsToTimestamp(timestamp) {
    const values = [];

    let rest = timestamp;

    // Hours 'XX:--:--'
    if (timestamp > 60 * 60) {
        const hours = Math.floor(rest / 3600);

        values.push(hours < 10 ? `0${hours}` : hours.toString());
        rest -= hours * 3600;
    }

    // Minutes '--:XX:--'
    if (rest / 60 > 0) {
        const minutes = Math.floor(rest / 60);

        values.push(minutes < 10 ? `0${minutes}` : minutes.toString());
        rest -= minutes * 60;
    } else {
        values.push('00');
    }

    // Seconds '--:--:XX'
    if (rest > 0) {
        values.push(rest < 10 ? `0${rest}` : rest.toString());
    } else {
        values.push('00');
    }

    return values.join(':');
}

/**
 *
 * @param {number} count
 * @returns {string} 's' if count is not 1, '' otherwise
 */
const pluralize = (count) => {
    return count === 1 ? '' : 's';
};

export { getAgo, ensureThreeLines, secondsToTimestamp, pluralize };
